import { createServerFn } from "@tanstack/react-start";
import { AppType } from "@/routes/api.$";
import { hc } from "hono/client";
import { getRequest } from "@tanstack/react-start/server";
import type { Region, SubscriptionData, CreditBalance } from "@/types/billing";
import * as z from "zod";
import {
  EditImageSchema,
  GenerateImageSchema,
} from "@/types/generation";
import { env } from "cloudflare:workers";
import { createDb } from "@/lib/db";
import { getServerSession } from "@/lib/session";
import { getSubscriptionByUserId } from "@/lib/data/subscription";
import { getCreditBalanceByUserId } from "@/lib/data/credits";
import { getGenerationsByUserId } from "@/lib/data/generations";
import { CreditActor } from "@/server/credit-actor";
import * as Sentry from "@sentry/cloudflare";
import { createRequestTimer, timeAsync } from "@/lib/perf";

// Type-safe wrapper for creating API client from request
function createApiClient(request: Request) {
  const url = new URL(request.url);

  // Convert Headers object to plain object for proper forwarding
  const headers: Record<string, string> = {};
  request.headers.forEach((value, key) => {
    // Skip problematic / hop-by-hop headers
    if (
      key === "content-length" ||
      key === "content-type" ||
      key === "host" ||
      key === "connection" ||
      key === "x-tsr-serverfn"
    ) {
      return;
    }
    headers[key] = value;
  });

  return hc<AppType>(url.origin, {
    headers,
  });
}

/**
 * Server function to fetch dashboard generations (first 6).
 * Uses shared data layer for direct database access.
 */
export const getServerDashboardGenerations = createServerFn({
  method: "GET",
})
  .inputValidator((data: { userId: string | undefined }) => data)
  .handler(async ({ data }) => {
    try {
      const db = createDb(env.DB);
      if (!data.userId) {
        throw new Error("Can not fetch images");
      }
      const result = await getGenerationsByUserId(db, data.userId, {
        page: 1,
        limit: 6,
      });

      // Transform imageUrl from R2 key to proxy URL
      const request = getRequest();
      const baseUrl = request ? new URL(request.url).origin : "";

      return {
        data: result.data.map((gen) => ({
          ...gen,
          imageUrl: gen.imageUrl.startsWith("http")
            ? gen.imageUrl // Legacy Replicate URL
            : `${baseUrl}/api/images/${gen.imageUrl}`, // R2 key to proxy URL
        })),
        pagination: result.pagination,
      };
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'server-dashboard-generations',
          userId: data.userId,
        },
        level: 'error',
      });
      console.error("[getServerDashboardGenerations] Exception:", error);
      return null;
    }
  });

/**
 * In-memory cache for credit balances with TTL
 */
const creditCache = new Map<string, { balance: CreditBalance; expires: number }>();

/**
 * Clear credit cache for a specific user
 */
export function clearCreditCache(userId: string) {
  creditCache.delete(`user:${userId}`);
}

/**
 * Server function to fetch user's credit balance.
 * Uses shared data layer for direct database access with in-memory caching.
 */
export const getServerCreditBalance = createServerFn({ method: "GET" }).handler(
  async (): Promise<CreditBalance | null> => {
    try {
      const session = await getServerSession();
      if (!session?.user) return null;

      const cacheKey = `user:${session.user.id}`;
      const now = Date.now();

      // Check cache first
      const cached = creditCache.get(cacheKey);
      if (cached && cached.expires > now) {
        return cached.balance;
      }

      // Cache miss or expired - fetch from CreditActor
      const actor = CreditActor.get(cacheKey);
      const balance = await getCreditBalanceByUserId(actor);

      // Store in cache with 30-second TTL
      if (balance) {
        creditCache.set(cacheKey, {
          balance,
          expires: now + 30000, // 30 seconds
        });
      }

      return balance;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'server-credit-balance',
        },
        level: 'error',
      });
      console.error("[getServerCreditBalance] Exception:", error);
      return null;
    }
  },
);

/**
 * Server function to fetch user's subscription details.
 * Uses shared data layer for direct database access.
 */
export const getServerSubscriptionDetails = createServerFn({
  method: "GET",
})
  .inputValidator((data: { userId: string | undefined }) => data)
  .handler(async ({ data }): Promise<SubscriptionData | null> => {
    try {
      const userId = data.userId;

      if (!userId) return null;

      const db = createDb(env.DB);
      return getSubscriptionByUserId(db, userId);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'server-subscription-details',
          userId: data.userId,
        },
        level: 'error',
      });
      console.error("[getServerSubscriptionDetails] Exception:", error);
      return null;
    }
  });

/**
 * Server function to fetch user's region based on IP geolocation.
 * Reads Cloudflare's cf object directly from the request.
 */
export const getServerRegion = createServerFn({ method: "GET" }).handler(
  async (): Promise<Region> => {
    try {
      const request = getRequest();
      if (!request) {
        console.warn("[getServerRegion] No request context, defaulting to EU");
        return "eu";
      }

      // Access Cloudflare's country code from the request
      const country = (request as any).cf?.country || "XX";

      // Map country code to region
      let region: Region;
      if (country === "US") {
        region = "us";
      } else if (country === "IN") {
        region = "in";
      } else {
        // Default to EU for all other countries
        region = "eu";
      }

      return region;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'server-region-detection',
        },
        level: 'warning',
      });
      console.error("[getServerRegion] Exception:", error);
      return "eu"; // Fallback to EU on error
    }
  },
);

/**
 * Server function to fetch paginated gallery generations.
 * Uses shared data layer for direct database access.
 */
export const getServerGalleryGenerations = createServerFn({
  method: "GET",
})
  .inputValidator((data: { userId: string | undefined }) => data)
  .handler(async ({ data }) => {
    try {
      const userId = data.userId;
      if (!userId) return null;

      const request = getRequest();
      if (!request) return null;

      // Extract page from request URL
      const url = new URL(request.url);
      const page = Number(url.searchParams.get("page")) || 1;
      const limit = Number(url.searchParams.get("limit")) || 20;

      const db = createDb(env.DB);
      const result = await getGenerationsByUserId(db, userId, {
        page,
        limit,
      });

      // Transform imageUrl from R2 key to proxy URL
      const baseUrl = url.origin;

      return {
        data: result.data.map((gen) => ({
          ...gen,
          imageUrl: gen.imageUrl.startsWith("http")
            ? gen.imageUrl // Legacy Replicate URL
            : `${baseUrl}/api/images/${gen.imageUrl}`, // R2 key to proxy URL
        })),
        pagination: result.pagination,
      };
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'server-gallery-generations',
          userId: data.userId,
        },
        level: 'error',
      });
      console.error("[getServerGalleryGenerations] Exception:", error);
      return null;
    }
  });

/**
 * Server action to generate a new image
 */
export const generateImageAction = createServerFn({ method: "POST" })
  .inputValidator(GenerateImageSchema)
  .handler(async ({ data }) => {
    const request = getRequest();
    if (!request) throw new Error("No request context");

    const perf = createRequestTimer(`sf-generate-${Date.now()}`);
    const api = createApiClient(request);
    perf("setup");

    const response = await timeAsync("generateImageAction", "api-roundtrip", () =>
      api.api.ai.image.new.$post({
        json: {
          prompt: data.prompt,
          modelId: data.modelId,
          parameters: data.parameters,
          referenceImages: data.referenceImages,
        },
      }),
    );
    perf("api-roundtrip");

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to generate image");
    }

    return response.json();
  });

/**
 * Server action to edit an existing image
 */
export const editImageAction = createServerFn({ method: "POST" })
  .inputValidator(EditImageSchema)
  .handler(async ({ data }) => {
    const request = getRequest();
    if (!request) throw new Error("No request context");

    const api = createApiClient(request);

    const response = await api.api.ai.image.edit.$post({
      json: { imageId: data.imageId, userInput: data.userInput },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to edit image");
    }

    return response.json();
  });

/**
 * Server function for generation chain
 */

export const fetchGenerationChain = createServerFn({ method: "GET" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const { id } = data;
    const request = getRequest();
    if (!request) throw new Error("No request context");

    const api = createApiClient(request);
    const response = await api.api.generation[":id"].chain.$get({
      param: { id },
    });
    if (!response) {
      throw new Error("Failed to fetch the edit history");
    }
    const result = await response.json();
    
    // Check for error response
    if ('error' in result) {
      throw new Error(result.error);
    }
    
    // Transform API response to match GenerationChain type (convert ISO strings to Dates)
    return {
      chain: result.chain.map((gen) => ({
        ...gen,
        createdAt: new Date(gen.createdAt),
      })),
      currentIndex: result.currentIndex,
    };
  });

/**
 * Zod schema for delete generation input
 */
const DeleteGenerationSchema = z.object({
  id: z.string().min(1, "Generation ID is required"),
});

/**
 * Server action to delete a generation
 */
export const deleteGenerationAction = createServerFn({ method: "POST" })
  .inputValidator(DeleteGenerationSchema)
  .handler(async ({ data }) => {
    const request = getRequest();
    if (!request) throw new Error("No request context");

    const api = createApiClient(request);

    const response = await api.api.generation[":id"].$delete({
      param: { id: data.id },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to delete generation");
    }

    return response.json();
  });
